import { usePageTitle } from '@/hooks/usePageTitle';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileText,
  Info,
  Loader2,
  Send,
  XCircle,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from '@/lib/toast';
import BlockRenderer from '@/components/BlockRenderer';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { A4_HEIGHT_PX, A4_WIDTH_PX } from '@/constants/a4';
import api from '@/lib/api';
import { QuoteClientLogoProvider } from '@/store/quoteClientLogoContext';
import type { PublicQuoteReview } from '@/types/review';

const feedbackStorageKey = (token: string) => `quote-review-feedback:${token}`;

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

function ScaledQuotePage({ scale, children }: { scale: number; children: ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(A4_HEIGHT_PX);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;
    const measure = () => setHeight(Math.max(A4_HEIGHT_PX, element.scrollHeight));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative shrink-0" style={{ width: A4_WIDTH_PX * scale, height: height * scale }}>
      <div
        ref={contentRef}
        className="absolute left-0 top-0 flex min-h-full flex-col overflow-hidden rounded-md bg-white shadow-[0_10px_30px_rgba(43,61,79,0.14)]"
        style={{
          width: A4_WIDTH_PX,
          minHeight: A4_HEIGHT_PX,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function QuoteReviewPage() {
  const { token = '' } = useParams<{ token: string }>();
  const previewRef = useRef<HTMLDivElement>(null);
  const [review, setReview] = useState<PublicQuoteReview | null>(null);
  usePageTitle('Offerte beoordelen', review?.quote.title);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(0.8);
  const [feedback, setFeedback] = useState('');
  const [draftSaved, setDraftSaved] = useState(false);
  const [submittingStatus, setSubmittingStatus] = useState<'approved' | 'rejected' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReview(await api.getPublicQuoteReview(token));
      setError(null);
    } catch (cause: unknown) {
      const data = (cause as { response?: { data?: { message?: string; error?: string } } })
        ?.response?.data;
      setError(data?.message || data?.error || 'Deze reviewlink is ongeldig.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const element = previewRef.current;
    if (!element) return;
    const measure = () => {
      if (element.clientWidth <= 0) return;
      const horizontalPadding = element.clientWidth >= 640 ? 64 : 32;
      setScale(Math.min(1, Math.max(0.28, (element.clientWidth - horizontalPadding) / A4_WIDTH_PX)));
    };
    measure();
    const animationFrame = window.requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [review]);

  useEffect(() => {
    if (!token) return;
    try {
      const storedFeedback = window.localStorage.getItem(feedbackStorageKey(token));
      if (storedFeedback) setFeedback(storedFeedback);
    } catch {
      // Reviewen blijft mogelijk wanneer lokale opslag door de browser is uitgeschakeld.
    }
  }, [token]);

  useEffect(() => {
    if (!token || !review?.actionable) return;
    try {
      if (feedback) {
        window.localStorage.setItem(feedbackStorageKey(token), feedback);
        setDraftSaved(true);
      } else {
        window.localStorage.removeItem(feedbackStorageKey(token));
        setDraftSaved(false);
      }
    } catch {
      setDraftSaved(false);
    }
  }, [feedback, review?.actionable, token]);

  const respond = async (status: 'approved' | 'rejected') => {
    if (status === 'rejected' && !feedback.trim()) return;
    setSubmittingStatus(status);
    try {
      const result = await api.respondToQuoteReview(
        token,
        status,
        status === 'rejected' ? feedback.trim() : undefined,
      );
      setReview((current) => current ? {
        ...current,
        status,
        actionable: false,
        feedback: result.feedback,
      } : current);
      try {
        window.localStorage.removeItem(feedbackStorageKey(token));
      } catch {
        // Geen actie nodig; de review zelf is al succesvol opgeslagen.
      }
      setDraftSaved(false);
      toast.success(status === 'approved' ? 'Akkoord doorgegeven' : 'Feedback doorgegeven');
    } catch (cause: unknown) {
      const data = (cause as { response?: { data?: { message?: string; error?: string } } })
        ?.response?.data;
      toast.error(data?.message || data?.error || 'Je reactie kon niet worden opgeslagen');
    } finally {
      setSubmittingStatus(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-3 bg-[#F5F6F8] text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Offerte voor review laden…
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F6F8] p-6">
        <div className="w-full max-w-md rounded-md border bg-white p-8 text-center">
          <AlertCircle className="mx-auto mb-4 h-8 w-8 text-destructive" />
          <h1 className="text-xl font-semibold">Review niet beschikbaar</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const decisionMessage = review.status === 'approved'
    ? 'Je hebt akkoord gegeven. De deal-eigenaar ontvangt bericht zodra alle reviewers klaar zijn.'
    : review.status === 'rejected'
      ? 'Je feedback is verstuurd naar de deal-eigenaar.'
      : null;

  const submitting = submittingStatus !== null;
  const pageCount = review.quote.blocks.length;
  const productCount = review.reviewer.productNames.length;

  return (
    <QuoteClientLogoProvider blocks={review.quote.blocks} layoutConfig={review.quote.layoutConfig}>
      <div className="min-h-screen bg-[#E9EDF1] lg:h-screen lg:overflow-hidden">
        <header className="sticky top-0 z-30 h-[72px] border-b bg-white lg:static">
          <div className="flex h-full items-center justify-between gap-4 px-4 sm:px-7">
            <div className="flex min-w-0 items-center gap-3.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-[#090932] p-1.5">
                <img src="/ubo/logo.svg" alt="UBO" className="h-full w-full object-contain" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-bold tracking-[-0.01em] text-[#2B3D4F]">
                  Offerte beoordelen
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {review.quote.title} · review voor {review.reviewer.name}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span className="hidden items-center gap-2 rounded bg-[#E0F3FA] px-2.5 py-1.5 text-xs font-semibold text-[#006B7C] sm:inline-flex">
                <span className="h-1.5 w-1.5 rounded-full bg-[#0091AE]" />
                Interne review
              </span>
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[#DDF3F8] text-xs font-bold text-[#087E95]"
                title={review.reviewer.name}
                aria-label={`Reviewer: ${review.reviewer.name}`}
              >
                {initials(review.reviewer.name)}
              </span>
            </div>
          </div>
        </header>

        <main className="flex min-h-[calc(100vh-72px)] flex-col lg:h-[calc(100vh-72px)] lg:min-h-0 lg:flex-row">
          <section
            ref={previewRef}
            aria-label="Offertevoorbeeld"
            className="order-2 min-w-0 flex-1 overflow-x-auto bg-[#E9EDF1] lg:order-1 lg:overflow-y-auto"
          >
            <div className="sticky left-0 top-0 z-10 flex min-w-full items-center justify-between gap-4 bg-[#E9EDF1] px-4 py-4 text-xs text-muted-foreground sm:px-8">
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden="true" />
                Alleen-lezen voorbeeld
              </span>
              <span className="tabular-nums">
                {pageCount} {pageCount === 1 ? 'pagina' : "pagina's"} · {Math.round(scale * 100)}%
              </span>
            </div>
            <div className="flex min-w-full flex-col items-center gap-5 px-4 pb-8 sm:px-8">
              {review.quote.blocks.map((block) => (
                <ScaledQuotePage key={block.id} scale={scale}>
                  <BlockRenderer
                    block={block}
                    variableMap={review.quote.variableMap}
                    isPublic
                    quoteStatus="draft"
                  />
                </ScaledQuotePage>
              ))}
            </div>
          </section>

          <aside
            aria-labelledby="review-heading"
            className="order-1 flex w-full shrink-0 flex-col border-b bg-white lg:order-2 lg:h-full lg:w-[420px] lg:overflow-hidden lg:border-b-0 lg:border-l"
          >
            <div className="border-b px-5 py-6 sm:px-7 sm:py-7">
              <h1
                id="review-heading"
                className="max-w-[330px] text-2xl font-bold leading-[1.25] tracking-[-0.02em] text-[#2B3D4F]"
              >
                Controleer de verkochte inzet
              </h1>
              <p className="mt-2 max-w-[360px] text-sm leading-5 text-muted-foreground">
                Bekijk de offerte en geef hier direct je oordeel. Tijdens het schrijven kun je door alle pagina’s blijven scrollen.
              </p>
            </div>

            <div className="border-b px-5 py-5 sm:px-7">
              <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                Jouw producten
              </p>
              <div className="space-y-2">
                {review.reviewer.productNames.map((name) => (
                  <div
                    key={name}
                    className="flex min-w-0 items-center justify-between gap-3 rounded bg-muted px-3.5 py-3 text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-2.5 font-semibold text-[#2B3D4F]">
                      <span className="h-2 w-2 shrink-0 rounded-full bg-[#0091AE]" />
                      <span className="truncate">{name}</span>
                    </span>
                    {productCount === 1 && (
                      <span className="shrink-0 text-xs font-semibold text-[#007B91]">1 product</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col px-5 py-5 sm:px-7 lg:overflow-y-auto">
              {review.status === 'invalidated' && (
                <Alert className="mb-5">
                  <Clock3 className="h-4 w-4" />
                  <AlertDescription>
                    De offerte is na deze aanvraag gewijzigd. Vraag de deal-eigenaar om een nieuwe reviewlink.
                  </AlertDescription>
                </Alert>
              )}

              {decisionMessage && (
                <Alert
                  className={review.status === 'approved'
                    ? 'mb-5 border-emerald-200 bg-emerald-50 text-emerald-900'
                    : 'mb-5 border-rose-200 bg-rose-50 text-rose-900'}
                >
                  {review.status === 'approved'
                    ? <CheckCircle2 className="h-4 w-4" />
                    : <XCircle className="h-4 w-4" />}
                  <AlertDescription>
                    {decisionMessage}
                    {review.status === 'rejected' && review.feedback && (
                      <span className="mt-2 block whitespace-pre-wrap font-medium">“{review.feedback}”</span>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {review.actionable && (
                <div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
                    <label htmlFor="review-feedback" className="text-sm font-bold text-[#2B3D4F]">
                      Feedback aan de deal-eigenaar
                    </label>
                    <span className="shrink-0 text-[11px] font-semibold text-[#B63E44]">
                      Verplicht bij wijzigingen
                    </span>
                  </div>
                  <Textarea
                    id="review-feedback"
                    value={feedback}
                    onChange={(event) => setFeedback(event.target.value)}
                    placeholder="Beschrijf concreet welke uren, bedragen of onderdelen aangepast moeten worden…"
                    maxLength={4000}
                    aria-describedby="review-feedback-help review-feedback-count"
                    className="mt-2 min-h-52 resize-y bg-white px-3.5 py-3 leading-5 shadow-none focus-visible:border-[#0091AE] focus-visible:ring-[#0091AE]"
                  />
                  <div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span id="review-feedback-help" className="flex min-w-0 items-center gap-2">
                      {draftSaved && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-600" />}
                      {draftSaved ? 'Concept automatisch bewaard' : 'Feedback wordt bewaard in deze browser'}
                    </span>
                    <span id="review-feedback-count" className="shrink-0 tabular-nums" aria-live="polite">
                      {feedback.length} / 4000
                    </span>
                  </div>
                </div>
              )}

              <div className="mt-auto pt-8">
                <div className="flex gap-2.5 rounded bg-[#E0F3FA] px-3.5 py-3 text-xs leading-[1.45] text-[#356574]">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#0087A0]" aria-hidden="true" />
                  <p>Deze persoonlijke reviewlink verloopt niet. Deel hem alleen met de bedoelde reviewer.</p>
                </div>
              </div>
            </div>

            {review.actionable && (
              <div className="shrink-0 border-t bg-white px-5 py-5 sm:px-7">
                <p className="mb-3 text-xs leading-[1.45] text-muted-foreground">
                  Je keuze wordt direct vastgelegd en per e-mail gedeeld met de deal-eigenaar.
                </p>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1.35fr)]">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => void respond('approved')}
                    disabled={submitting}
                    className="px-3"
                  >
                    {submittingStatus === 'approved'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <CheckCircle2 className="h-4 w-4" />}
                    Akkoord geven
                  </Button>
                  <Button
                    variant="destructive"
                    size="lg"
                    onClick={() => void respond('rejected')}
                    disabled={!feedback.trim() || submitting}
                    className="px-3 shadow-[0_5px_14px_rgba(255,112,82,0.18)]"
                  >
                    {submittingStatus === 'rejected'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Send className="h-4 w-4" />}
                    Wijzigingen versturen
                  </Button>
                </div>
              </div>
            )}
          </aside>
        </main>
      </div>
    </QuoteClientLogoProvider>
  );
}
